import {
  colors,
  cornerRadius,
  elevations,
  keyframes,
  spacing,
  styled,
} from "../../../../stitches.config";
import { BaseButton } from "./BaseButton";

const BORDER_WIDTH = 1;
const DEFAULT_TARGET_SCALE = 0.98;
const PADDING_VERTICAL = {
  small: 6,
  medium: 6,
  large: 10,
} as const;
const PADDING_ICON_ONLY = {
  small: 8,
  medium: 10,
  large: 14,
} as const;
const PADDING_HORIZONTAL = {
  small: 8,
  medium: 10,
  large: 14,
} as const;

export const ButtonContentContainer = styled("div", {
  display: "flex",
  flexDirection: "row",
  gap: spacing.s,
  alignItems: "center",
  justifyContent: "center",
});

export const LoadingOverlayContainer = styled("div", {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const spin = keyframes({
  "0%": { transform: "rotate(0deg)" },
  "100%": { transform: "rotate(360deg)" },
});

export const LoadingWrapper = styled("div", {
  animation: `${spin} 1s infinite linear`,
  display: "flex",
  alignItems: "center",
});

export const ButtonContentWrapper = styled("div", {
  opacity: 1,

  variants: {
    isPending: {
      true: {
        opacity: 0,
      },
      false: {
        opacity: 1,
      },
    },
  },
});

export const StyledButton = styled(BaseButton, {
  transition: "transform 100ms ease-out",
  '&[data-pressed="true"]': {
    transform: `scale(${DEFAULT_TARGET_SCALE})`,
  },
  borderRadius: cornerRadius.radiusMd,
  boxShadow: elevations.sm,
  position: "relative",

  variants: {
    iconOnly: {
      true: {}, // Empty variant used for compound variants
      false: {},
    },
    hasIconStart: {
      true: {}, // Empty variant used for compound variants
      false: {},
    },
    hasIconEnd: {
      true: {}, // Empty variant used for compound variants
      false: {},
    },
    variant: {
      primary: {
        backgroundColor: colors.blue500.value,
        "&[data-hovered], &[data-pressed='true']": {
          backgroundColor: colors.blue600.value,
        },
      },
      secondary: {
        backgroundColor: colors.bgUiSurface.value,
        "&[data-hovered], &[data-pressed='true']": {
          borderColor: colors.borderUi.value,
        },
      },
      critical: {
        backgroundColor: colors.red500.value,
        "&[data-hovered], &[data-pressed='true']": {
          backgroundColor: colors.red600.value,
        },
      },
      outline: {
        backgroundColor: "transparent",
        borderWidth: `${BORDER_WIDTH}px`,
        borderStyle: "solid",
        borderColor: colors.grey300.value,
        "&[data-hovered], &[data-pressed='true']": {
          borderColor: colors.grey400.value,
        },
      },
      outlineInverted: {
        backgroundColor: "transparent",
        borderWidth: `${BORDER_WIDTH}px`,
        borderStyle: "solid",
        borderColor: colors.white.value,
        "&[data-hovered], &[data-pressed='true']": {
          borderColor: colors.grey400.value,
        },
      },
    },
    size: {
      small: {
        paddingTop: `${PADDING_VERTICAL.small}px`,
        paddingBottom: `${PADDING_VERTICAL.small}px`,
        paddingLeft: spacing.m,
        paddingRight: spacing.m,
      },
      medium: {
        paddingTop: `${PADDING_VERTICAL.medium}px`,
        paddingBottom: `${PADDING_VERTICAL.medium}px`,
        paddingLeft: spacing.m,
        paddingRight: spacing.m,
      },
      large: {
        paddingTop: `${PADDING_VERTICAL.large}px`,
        paddingBottom: `${PADDING_VERTICAL.large}px`,
        paddingLeft: spacing.m,
        paddingRight: spacing.m,
      },
    },
  },

  compoundVariants: [
    {
      iconOnly: true,
      size: "small",
      css: {
        padding: `${PADDING_ICON_ONLY.small}px`,
      },
    },
    {
      iconOnly: true,
      size: "medium",
      css: {
        padding: `${PADDING_ICON_ONLY.medium}px`,
      },
    },
    {
      iconOnly: true,
      size: "large",
      css: {
        padding: `${PADDING_ICON_ONLY.large}px`,
      },
    },
    {
      hasIconStart: true,
      size: "small",
      css: {
        paddingLeft: `${PADDING_HORIZONTAL.small}px`,
      },
    },
    {
      hasIconStart: true,
      size: "medium",
      css: {
        paddingLeft: `${PADDING_HORIZONTAL.medium}px`,
      },
    },
    {
      hasIconStart: true,
      size: "large",
      css: {
        paddingLeft: `${PADDING_HORIZONTAL.large}px`,
      },
    },
    {
      hasIconEnd: true,
      size: "small",
      css: {
        paddingRight: `${PADDING_HORIZONTAL.small}px`,
      },
    },
    {
      hasIconEnd: true,
      size: "medium",
      css: {
        paddingRight: `${PADDING_HORIZONTAL.medium}px`,
      },
    },
    {
      hasIconEnd: true,
      size: "large",
      css: {
        paddingRight: `${PADDING_HORIZONTAL.large}px`,
      },
    },
  ],

  "&[data-disabled='true'], &[aria-disabled='true']": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
});

export type StyledButtonProps = typeof StyledButton;
